/**
 * OMNI DECISION TWIN — presentation only.
 *
 * Traditional bid leveling asks "who is cheapest?". This asks a different
 * question: WHAT PROJECT STATE EXISTS IF YOU MAKE THIS DECISION?
 *
 * `counterfactual-execution-1` answers it by overlaying assumptions onto
 * snapshots and re-running the same pure cores the live surfaces use. It
 * writes nothing, has no apply path, and labels every response HYPOTHETICAL.
 *
 * WHAT THIS MODULE REFUSES TO DO
 *
 * - It does not rank scenarios. There is no winner, no best, no score. Each
 *   scenario is an independent backend answer rendered on its own terms.
 * - It does not compare scenarios to each other. The backend compares a
 *   scenario to the BASELINE and to nothing else; a frontend that ranked two
 *   projections against one another would be inventing the comparison.
 * - It does not read a delta as causality. "With this assumption, gate 8
 *   passes and the finish moves" is arithmetic. "This provider unlocks the
 *   job" is a causal claim no contract owns.
 */
import { RUNTIME_STATES, runtimeStateDisplay, presentContractVersion } from './runtimeVocabulary';

export const DECISION_TWIN_CONTRACT = 'counterfactual-execution-1';

/** The closed scenario grammar. Unknown kinds are refused by the backend. */
export const SCENARIO_KINDS = [
  'REQUIREMENT_CLEARED',
  'MATERIAL_ARRIVED',
  'PREDECESSOR_COMPLETE',
  'PROVIDER_SELECTED',
];

/**
 * Provenance, as the ledger reports it.
 *
 * HUMAN_HYPOTHESIS marks the swear-in fields — physical observations only the
 * site can produce, assumed under an explicit flag. It must never look like a
 * scenario assumption the engine derived.
 */
export const ASSUMPTION_PROVENANCE = {
  SCENARIO_ASSUMPTION: {
    label: 'Scenario assumption',
    meaning: 'An overlay applied for this simulation only.',
    human: false,
  },
  HUMAN_HYPOTHESIS: {
    label: 'Human hypothesis',
    meaning:
      'A physical fact only the site can establish, assumed here because a person asked for it. Nothing has verified it.',
    human: true,
  },
};

export const assumptionProvenanceDisplay = (provenance) =>
  ASSUMPTION_PROVENANCE[provenance] || ASSUMPTION_PROVENANCE.SCENARIO_ASSUMPTION;

/**
 * One ledger entry, rendered verbatim.
 *
 * A field appears only when the overlay actually changed it — a pure
 * restatement has empty fields, and that is worth showing rather than hiding,
 * because "this assumption changed nothing" is itself an answer.
 */
export const presentAssumption = (entry) => {
  const e = entry || {};
  const fields = e.fields && typeof e.fields === 'object' ? e.fields : {};
  const changes = Object.keys(fields).map((field) => ({
    field,
    from: fields[field]?.from ?? null,
    to: fields[field]?.to ?? null,
  }));
  return {
    kind: e.kind || null,
    target: e.target && typeof e.target === 'object' ? e.target : {},
    changes,
    /** An overlay that moved nothing is not a failure — it is a restatement. */
    changedNothing: changes.length === 0,
    provenance: e.provenance || 'SCENARIO_ASSUMPTION',
    provenanceDisplay: assumptionProvenanceDisplay(e.provenance),
  };
};

/**
 * Counts per runtime state, in the vocabulary's fixed order.
 *
 * Never sorted by size. A missing state reports 0 because the backend's counts
 * map is over a closed set — but an absent MAP is null, not a set of zeros,
 * because "the backend did not answer" and "the backend answered zero" are
 * different facts.
 */
export const presentCounts = (counts) => {
  if (!counts || typeof counts !== 'object') return null;
  return RUNTIME_STATES.map((state) => ({
    state,
    label: runtimeStateDisplay(state).label,
    count: typeof counts[state] === 'number' ? counts[state] : 0,
  }));
};

/**
 * One classification change.
 *
 * `from` and `to` are OBJECTS carrying status, canStart and the governing
 * constraint — not bare status strings. Reading them as strings would drop
 * the two fields that say WHY the classification moved.
 */
export const presentClassificationChange = (change) => {
  const c = change || {};
  const side = (s) => ({
    status: RUNTIME_STATES.includes(s?.status) ? s.status : 'UNKNOWN',
    canStart: typeof s?.canStart === 'boolean' ? s.canStart : null,
    governingConstraint: s?.governingConstraint || null,
  });
  return {
    instanceId: c.instanceId || null,
    instanceName: c.instanceName || null,
    from: side(c.from),
    to: side(c.to),
  };
};

/**
 * Changes grouped by the state they moved TO.
 *
 * Deliberately worded as arithmetic. This says "these instances are classified
 * differently under this scenario" — not "this decision unlocks them", which
 * would be the causal claim the contract forbids.
 *
 * Groups follow the vocabulary's order, so the grouping can never read as a
 * ranking of outcomes.
 */
export const groupChangesByOutcome = (changes) => {
  const rows = (Array.isArray(changes) ? changes : []).map(presentClassificationChange);
  return RUNTIME_STATES.map((state) => ({
    state,
    label: runtimeStateDisplay(state).label,
    changes: rows.filter((r) => r.to.status === state),
  })).filter((g) => g.changes.length > 0);
};

/**
 * The whole scenario response.
 *
 * `hypothetical` is computed defensively rather than trusted: a projection
 * rendered as current state is the single most dangerous thing this surface
 * could do, so anything short of an explicit, consistent "this was not
 * applied" is treated as hypothetical anyway.
 */
export const presentScenario = (payload) => {
  const p = payload || {};
  const basis = p.projectionBasis || {};
  const delta = p.delta || {};
  return {
    contract: presentContractVersion(p.contractVersion, DECISION_TWIN_CONTRACT),
    /** Always true for this surface. Never inferred as false from an absence. */
    hypothetical: true,
    label: p.label || 'HYPOTHETICAL',
    /**
     * The contract promises this is false. If it were ever true the screen
     * must not quietly carry on — the caller renders the alarm.
     */
    appliedToCanonicalState: p.appliedToCanonicalState === true,
    projectId: p.projectId || null,
    computedAt: p.computedAt || null,

    /** What the projection was computed over — shown above any number. */
    basis: {
      sourceFingerprint: basis.sourceFingerprint || null,
      projectStart: basis.projectStart || null,
      scenario: Array.isArray(basis.scenario) ? basis.scenario : [],
      /**
       * A disclosure, not a footnote: these were NOT re-derived, so a
       * consequence the simulator cannot honestly compute is absent rather
       * than approximated.
       */
      heldAtPersisted: Array.isArray(basis.heldAtPersisted)
        ? basis.heldAtPersisted.filter(Boolean)
        : [],
    },

    assumptions: (Array.isArray(p.assumptionLedger) ? p.assumptionLedger : []).map(
      presentAssumption
    ),

    baselineCounts: presentCounts(p.baseline?.startability?.counts),
    projectedCounts: presentCounts(p.projected?.startability?.counts),

    schedule: {
      baselineFeasible: typeof p.baseline?.schedule?.feasible === 'boolean'
        ? p.baseline.schedule.feasible
        : null,
      projectedFeasible: typeof p.projected?.schedule?.feasible === 'boolean'
        ? p.projected.schedule.feasible
        : null,
      finishBefore: delta.projectFinishBefore || null,
      finishAfter: delta.projectFinishAfter || null,
      /** The contract's own field: the new finish, or null when unmoved. */
      finishMoved: delta.projectFinishMoved || null,
    },

    changes: groupChangesByOutcome(delta.classificationChanges),
    changeCount: Array.isArray(delta.classificationChanges)
      ? delta.classificationChanges.length
      : 0,

    missingInformation: Array.isArray(p.missingInformation)
      ? p.missingInformation.filter(Boolean)
      : [],
    deterministic: p.deterministic === true,
    aiDetermined: p.aiDetermined === true,
  };
};

/**
 * Which quotes a provider scenario may even be run against.
 *
 * The backend refuses a dead quote (`quote_not_live`) exactly as the real
 * authorize path would. Offering the button anyway and letting it fail would
 * teach an estimator that OMNΛS is unreliable; not saying WHY it is absent
 * would be worse.
 */
export const presentQuoteForScenario = (quote) => {
  const q = quote || {};
  const live = q.isCurrent === true && q.status === 'SUBMITTED';
  return {
    quoteId: q.quoteId || null,
    bidPackageId: q.bidPackageId || null,
    bidderDisplayName: q.bidderDisplayName || q.bidderKey || null,
    status: q.status || null,
    isCurrent: q.isCurrent === true,
    revision: typeof q.revision === 'number' ? q.revision : null,
    /**
     * Commercial facts are shown BESIDE the projection, never fused into it.
     * A null amount stays null — a quote without a price is not a free quote.
     */
    baseAmount: typeof q.baseAmount === 'number' ? q.baseAmount : null,
    currency: q.currency || null,
    complianceState: q.complianceState || null,
    simulatable: live,
    notSimulatableReason: live
      ? null
      : q.isCurrent === true
        ? `This quote is ${q.status || 'not submitted'}. OMNΛS can only simulate a live quote, the same rule the award path applies.`
        : 'This is a superseded revision. Only the current quote can be simulated.',
  };
};

/**
 * Why a scenario was refused, kept as distinct answers.
 *
 * The simulator's refusals are meaningful product events, not errors: each one
 * names a rule the real award path also applies. Collapsing them into
 * "simulation failed" would discard the reason.
 */
export const presentScenarioRefusal = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const detail = typeof data === 'object' && data ? data.detail : null;
  const code =
    (typeof data === 'object' && data && data.code) ||
    (typeof detail === 'object' && detail && detail.code) ||
    null;
  const message = typeof detail === 'string' ? detail : detail?.message || null;

  const refusal = (kind, title, body) => ({ kind, title, detail: body, code, message });

  if (code === 'contradicts_recorded_state') {
    return refusal(
      'CONTRADICTS_RECORD',
      'That has already happened',
      'A recorded fact cannot be assumed away. OMNΛS keeps what was recorded and names both values rather than letting a scenario overwrite history.'
    );
  }
  if (code === 'quote_not_live') {
    return refusal(
      'QUOTE_NOT_LIVE',
      'That quote is not live',
      'The award path would refuse this quote, so the simulator refuses it too. Simulating a dead quote would model a decision nobody could make.'
    );
  }
  if (code === 'no_apply_path') {
    return refusal(
      'NO_APPLY_PATH',
      'This simulation cannot be applied',
      'There is no apply path, by design and permanently. A scenario changes nothing; an authorized human makes the decision through the award flow.'
    );
  }
  if (code === 'terminal_state') {
    return refusal(
      'TERMINAL_STATE',
      'That requirement is closed',
      'It is cancelled or superseded. The live path cannot produce that clearance, so the simulator will not either.'
    );
  }
  if (code === 'unknown_target' || code === 'unknown_scenario_kind') {
    return refusal(
      'UNKNOWN_TARGET',
      'OMNΛS does not recognise that assumption',
      'Unknown rows and unknown scenario kinds are refused rather than guessed at.'
    );
  }
  if (code === 'invalid_assumption') {
    return refusal(
      'INVALID_ASSUMPTION',
      'That assumption is not one OMNΛS can make',
      message || 'The scenario grammar is closed, and this combination is outside it.'
    );
  }
  if (status === 409 || code === 'no_execution_schedule') {
    return refusal(
      'NO_EXECUTION_SCHEDULE',
      'This project has no execution work to simulate',
      'The execution graph has not been instantiated. That is an absence of records — not a project where everything can start.'
    );
  }
  if (status === 404) {
    return refusal(
      'NOT_FOUND',
      'This project was not found',
      'It may belong to another tenant, or it may not exist. OMNΛS answers the same way for both, deliberately.'
    );
  }
  if (status === 503 || code === 'not_enabled') {
    return refusal(
      'NOT_ENABLED',
      'The execution runtime is switched off',
      'A platform-wide setting. Nothing about this project or this quote caused it.'
    );
  }
  if (status === 403 && code === 'tenant_not_entitled') {
    return refusal(
      'NOT_ENTITLED',
      'This tenant is not entitled to the execution runtime',
      'An OMNΛS operator grants this.'
    );
  }
  if (status === 403 && code === 'capability_required') {
    return refusal(
      'CAPABILITY_REQUIRED',
      'You do not have the capability this simulation needs',
      'Simulating needs execution.simulate, and a provider scenario additionally needs the same access as bid pricing — this surface does not reopen that door.'
    );
  }
  if (status === 403) {
    return refusal(
      'ROLE_REFUSED',
      'Your role cannot run this simulation',
      'Someone with commercial access on this project can.'
    );
  }
  return refusal(
    'UNAVAILABLE',
    'The simulation could not be run',
    'This is a problem reaching OMNΛS, not a statement about the decision.'
  );
};
